import { Injectable } from '@angular/core';
import { BulkOp } from '../grid-types';

export type RowOp = 'update'|'delete'|'undelete'|'create';
type RowPatch = { __op?: RowOp } & Record<string, any>;

@Injectable({ providedIn: 'root' })
export class GridEditStore {
  private store = new Map<string, Map<string, RowPatch>>(); // gridKey -> rowId -> patch
  private tempSeq = 0;

  newTempId(): string {
    this.tempSeq += 1;
    return `__new_${Date.now()}_${this.tempSeq}`;
  }

  upsert(gridKey: string, rowId: string, field: string, value: any) {
    if (!rowId || rowId === 'undefined' || rowId === 'null') return; // hard guard

    const g = this.store.get(gridKey) ?? new Map<string, RowPatch>();
    const patch = g.get(rowId) ?? {};

    if (patch.__op === 'delete') return;

    // Keep create as create
    if (patch.__op !== 'create') {
      patch.__op = 'update';
    }

    patch[field] = value;

    g.set(rowId, patch);
    this.store.set(gridKey, g);
  }

  stageCreate(gridKey: string, tempId: string, values: Record<string, any>, idField: string) {
    const g = this.store.get(gridKey) ?? new Map<string, RowPatch>();

    // ensure the row has its ID field so inline edits compute rowId correctly
    const clean = { ...values, [idField]: tempId };

    g.set(tempId, { __op: 'create', ...clean, __isNew: true });
    this.store.set(gridKey, g);
  }


  markDelete(gridKey: string, rowId: string) {
    const g = this.store.get(gridKey) ?? new Map<string, RowPatch>();
    const patch = g.get(rowId) ?? {};
    patch.__op = 'delete';
    for (const k of Object.keys(patch)) if (k !== '__op') delete patch[k];
    g.set(rowId, patch);
    this.store.set(gridKey, g);
  }

  undoDelete(gridKey: string, rowId: string) {
    const g = this.store.get(gridKey);
    if (!g) return;
    const patch = g.get(rowId);
    if (!patch) return;

    if (patch.__op === 'delete') {
      // if it was new and deleted, just remove it
      // (new rows are tracked separately as staged create entries)
      delete patch.__op;
    } else {
      delete patch.__op;
    }

    if (Object.keys(patch).length === 0) g.delete(rowId);
  }

  isRowDeleted(gridKey: string, rowId: string) {
    const g = this.store.get(gridKey);
    return g?.get(rowId)?.__op === 'delete';
  }

  isRowNew(gridKey: string, rowId: string) {
    const g = this.store.get(gridKey);
    return g?.get(rowId)?.__op === 'create';
  }

  isCellDirty(gridKey: string, rowId: string, field: string) {
    const g = this.store.get(gridKey);
    const p = g?.get(rowId);
    return !!p && p.__op !== 'delete' && Object.prototype.hasOwnProperty.call(p, field);
  }

  apply(gridKey: string, idField: string, rows: Record<string, any>[]) {
    const g = this.store.get(gridKey);
    if (!g) return rows;

    const merged = rows.map(r => {
      const id = String(r[idField]);
      const patch = g.get(id);
      if (!patch) return r;
      if (patch.__op === 'delete') return { ...r, __rowDeleted: true };
      const { __op, ...rest } = patch;
      return { ...r, ...rest };
    });

    for (const [rowId, patch] of g.entries()) {
      if (patch.__op !== 'create') continue;
      const { __op, __isNew, ...rest } = patch;
      merged.unshift({ ...rest, [idField]: rowId, __isNew: true });
    }

    return merged;
  }

  getOps(gridKey: string, idField?: string): BulkOp[] {
    const g = this.store.get(gridKey);
    if (!g) return [];

    const ops: BulkOp[] = [];
    for (const [rowId, patch] of g.entries()) {
      const { __op, __isNew, ...rest } = patch;

      if (__op === 'delete') ops.push({ rowId, op: 'Delete' });
      else if (__op === 'undelete') ops.push({ rowId, op: 'Undelete' });
      else if (__op === 'create') {
        if (idField && rest[idField] == null) rest[idField] = rowId;
        ops.push({ rowId, op: 'Create', patch: rest });
      }
      else {
        const keys = Object.keys(rest);
        if (keys.length > 0) ops.push({ rowId, op: 'Update', patch: rest });
      }
    }
    return ops;
  }

  dirtyCount(gridKey: string): number {
    const g = this.store.get(gridKey);
    if (!g) return 0;
    let n = 0;
    for (const patch of g.values()) {
      if (patch.__op === 'delete' || patch.__op === 'create') n += 1;
      else n += Object.keys(patch).filter(k => k !== '__op').length;
    }
    return n;
  }

  clear(gridKey: string) { this.store.delete(gridKey); }

  remove(gridKey: string, rowId: string) {
    this.store.get(gridKey)?.delete(rowId);
  }
}
